// src/merchant/MerchantOrders.js
import React, { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs, doc,
  updateDoc, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import {
  Search, ChevronDown, ChevronUp, Package,
  Printer, FileText, Phone, MapPin, CreditCard,
  Bell, BellOff,
} from 'lucide-react';
import { generateShippingLabel, generatePackingSlip } from '../utils/pdfGenerator';
import { startMerchantOrderListener, stopMerchantOrderListener, sendShippedNotification } from '../utils/notifications';
import toast from 'react-hot-toast';

const STATUSES = ['confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const STATUS_STYLES = {
  confirmed:  { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Confirmed'  },
  processing: { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Processing' },
  shipped:    { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Shipped'    },
  delivered:  { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Delivered'  },
  cancelled:  { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Cancelled'  },
};

function OrderCard({ order, merchantProfile, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const st   = STATUS_STYLES[order.status] || STATUS_STYLES.confirmed;
  const addr = order.shippingAddress || {};
  const date = order.createdAt?.toDate?.()?.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) || 'Recently';

  const handleStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'merchantOrders', order.id), {
        status:        newStatus,
        trackingStatus: newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
      });
      onStatusChange(order.id, newStatus);

      // Send shipped email notification
      if (newStatus === 'shipped') {
        await sendShippedNotification(order);
      }
      toast.success(`Order marked as ${newStatus}`);
    } catch { toast.error('Failed to update status'); }
    finally { setUpdating(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Row header */}
      <div className="flex flex-wrap items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        {/* Item images */}
        <div className="flex -space-x-2 shrink-0">
          {order.items?.slice(0, 2).map((item, i) => (
            <img key={i} src={item.image} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white" />
          ))}
          {(order.items?.length || 0) > 2 && (
            <div className="w-10 h-10 rounded-xl bg-rose-50 border-2 border-white flex items-center justify-center text-rose-500 text-xs font-bold">
              +{order.items.length - 2}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-body font-bold text-gray-900 text-sm">#{order.id.slice(-8).toUpperCase()}</p>
          <p className="text-gray-400 font-body text-xs">{date} · {addr.name} · {order.items?.length} item(s)</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <p className="font-display font-bold text-gray-900">₹{order.total?.toLocaleString('en-IN')}</p>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
          {/* Payment badge */}
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
            order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {order.paymentStatus === 'paid' ? '✓ Paid' : 'COD'}
          </span>
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-gray-100 p-5 space-y-4">

          {/* ── Customer details ── */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-rose-50/60 rounded-2xl p-4">
              <p className="font-body font-semibold text-gray-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <MapPin size={13} className="text-rose-400" /> Customer & Delivery
              </p>
              <p className="font-body font-bold text-gray-900 text-base">{addr.name || '—'}</p>
              <p className="font-body text-gray-600 text-sm mt-1 leading-relaxed">
                {addr.address}<br />
                {addr.city}, {addr.state} — {addr.pincode}
              </p>
              <p className="font-body text-gray-700 text-sm font-semibold mt-2 flex items-center gap-1.5">
                <Phone size={14} className="text-rose-400" />
                <a href={`tel:${addr.phone}`} className="hover:text-rose-500 transition-colors">{addr.phone}</a>
              </p>
              <p className="font-body text-gray-400 text-xs mt-1">{order.userEmail}</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="font-body font-semibold text-gray-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <CreditCard size={13} className="text-green-500" /> Payment Info
              </p>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold mb-2 ${
                order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {order.paymentStatus === 'paid' ? '✓ PREPAID — DO NOT collect cash' : '💵 COD — Collect ₹' + order.total?.toLocaleString('en-IN')}
              </div>
              {order.paymentId && (
                <p className="font-body text-gray-400 text-xs mt-2">
                  Payment ID: <span className="font-mono">{order.paymentId}</span>
                </p>
              )}
              {order.awbCode && (
                <p className="font-body text-gray-600 text-sm mt-1">
                  AWB: <strong>{order.awbCode}</strong>
                </p>
              )}
              {order.courierName && (
                <p className="font-body text-gray-500 text-xs">Via: {order.courierName}</p>
              )}
            </div>
          </div>

          {/* ── Items ── */}
          <div>
            <p className="font-body font-semibold text-gray-700 text-sm mb-2">Items to Ship</p>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-semibold text-gray-900 text-sm line-clamp-1">{item.name}</p>
                    <p className="text-xs text-gray-400 font-body">Qty: {item.quantity} × ₹{item.price?.toLocaleString('en-IN')}</p>
                  </div>
                  <p className="font-body font-bold text-gray-900 text-sm shrink-0">
                    ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Print buttons ── */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => generateShippingLabel(order, merchantProfile)}
              className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-4 py-2.5 rounded-xl font-body font-semibold text-sm transition-all">
              <Printer size={15} /> Print Shipping Label
            </button>
            <button onClick={() => generatePackingSlip(order, merchantProfile)}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-body font-semibold text-sm transition-all">
              <FileText size={15} /> Packing Slip
            </button>
          </div>

          {/* ── Status update ── */}
          <div>
            <p className="font-body font-semibold text-gray-700 text-sm mb-2">Update Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => {
                const sst = STATUS_STYLES[s];
                const cur = order.status === s;
                return (
                  <button key={s} onClick={() => !cur && handleStatus(s)}
                    disabled={updating || cur}
                    className={`px-4 py-2 rounded-xl text-xs font-body font-bold transition-all ${
                      cur
                        ? `${sst.bg} ${sst.text} ring-2 ring-offset-1 ring-current`
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50'
                    }`}>
                    {sst.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Packaging reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-amber-700 font-body text-xs font-semibold mb-0.5">📦 Packaging Checklist</p>
            <ul className="text-amber-600 font-body text-xs space-y-0.5">
              <li>• Pack items securely with bubble wrap if needed</li>
              <li>• Attach the printed shipping label on top</li>
              <li>• Keep the packing slip inside the package</li>
              <li>• Mark as "Shipped" after handing to courier</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MerchantOrders() {
  const { user }              = useApp();
  const [orders,        setOrders]        = useState([]);
  const [merchantProfile, setMerchantProfile] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [filter,        setFilter]        = useState('all');
  const [notifications, setNotifications] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        // Fetch merchant profile for shipping label
        const profileSnap = await getDoc(doc(db, 'merchants', user.uid));
        if (profileSnap.exists()) setMerchantProfile(profileSnap.data());

        // Fetch only this merchant's orders
        const q    = query(collection(db, 'merchantOrders'), where('merchantId', '==', user.uid));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setOrders(list);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup listener on unmount
    return () => stopMerchantOrderListener();
  }, [user]);

  const toggleNotifications = () => {
    if (!notifications) {
      startMerchantOrderListener(user.uid, (count) => {
        toast.success(`🛍️ ${count} new order${count > 1 ? 's' : ''}!`);
      });
      setNotifications(true);
      toast.success('Order notifications enabled 🔔');
    } else {
      stopMerchantOrderListener();
      setNotifications(false);
      toast('Notifications paused');
    }
  };

  const onStatusChange = (id, status) =>
    setOrders((prev) => prev.map((o) => o.id === id ? { ...o, status } : o));

  const filtered = orders.filter((o) => {
    const ms = !search ||
      o.id.toLowerCase().includes(search.toLowerCase()) ||
      o.shippingAddress?.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.shippingAddress?.phone?.includes(search);
    const mf = filter === 'all' || o.status === filter;
    return ms && mf;
  });

  const counts = orders.reduce((acc, o) => {
    acc[o.status || 'confirmed'] = (acc[o.status || 'confirmed'] || 0) + 1;
    return acc;
  }, {});

  const revenue = orders.filter((o) => o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">My Orders</h2>
          <p className="text-gray-400 font-body text-sm">
            {orders.length} orders · Revenue: <span className="text-green-600 font-semibold">₹{revenue.toLocaleString('en-IN')}</span>
          </p>
        </div>
        <button onClick={toggleNotifications}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-body font-semibold transition-all border ${
            notifications
              ? 'bg-rose-50 text-rose-600 border-rose-200'
              : 'bg-white text-gray-500 border-gray-200 hover:border-rose-300'
          }`}>
          {notifications ? <Bell size={16} className="animate-pulse" /> : <BellOff size={16} />}
          {notifications ? 'Alerts ON' : 'Enable Alerts'}
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2">
        {[{ key: 'all', label: 'All', count: orders.length },
          ...STATUSES.map((s) => ({ key: s, label: STATUS_STYLES[s].label, count: counts[s] || 0 }))
        ].map(({ key, label, count }) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-body font-medium transition-all ${
              filter === key
                ? 'bg-rose-500 text-white shadow-[0_4px_12px_rgba(244,63,94,0.3)]'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-rose-300'
            }`}>
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${filter === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order ID, customer name or phone..."
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-sm" />
      </div>

      {/* Orders */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <Package size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="font-body font-medium text-gray-500">No orders found</p>
          <p className="font-body text-gray-400 text-sm mt-1">Orders for your products will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              merchantProfile={merchantProfile}
              onStatusChange={onStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
